import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, CreditCard, Sparkles, Clock } from "lucide-react";

const MODES = ["supportive", "direct", "strategic", "encouraging", "accountable"];

export default function Settings() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [mode, setMode] = useState(user?.coaching_mode || "supportive");
  const [saving, setSaving] = useState(false);
  const [ent, setEnt] = useState(null);

  useEffect(() => { api.get("/me/entitlements").then(({ data }) => setEnt(data)).catch(() => {}); }, [user]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("checkout") === "success" && p.get("session_id")) {
      poll(p.get("session_id"), 0);
    }
  }, []);

  const poll = async (sid, n) => {
    if (n > 6) return;
    try {
      const { data } = await api.get(`/subscriptions/status/${sid}`);
      if (data.payment_status === "paid") {
        toast.success(`You're on the ${data.plan} plan!`);
        const { data: me } = await api.get("/auth/me");
        setUser(me.user);
        window.history.replaceState({}, "", "/app/settings");
        return;
      }
    } catch { /* ignore */ }
    setTimeout(() => poll(sid, n + 1), 1800);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { name, coaching_mode: mode });
      setUser(data.user);
      toast.success("Settings saved.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <h1 className="font-nunito font-extrabold tracking-tight text-4xl text-[#2D3748] sm:text-5xl">Settings</h1>

      <div className="rounded-3xl border border-[#FFE8E3] bg-white p-7">
        <h2 className="font-nunito text-lg font-bold tracking-tight text-[#2D3748]">Profile</h2>
        <div className="mt-4 space-y-4">
          <label className="block text-sm text-[#718096]">Name
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name"
              className="mt-1 h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
          </label>
          <label className="block text-sm text-[#718096]">Coaching style
            <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="settings-mode"
              className="mt-1 h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 capitalize text-[#2D3748] outline-none focus:border-[#FF7E67]">
              {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <p className="text-xs text-[#718096]">Email: {user?.email} • Signed in via {user?.auth_provider}</p>
          <button onClick={save} disabled={saving} data-testid="save-settings-btn"
            className="flex items-center gap-2 rounded-full bg-sunrise px-6 py-3 text-sm font-bold tracking-wide text-white shadow-glow-coral disabled:opacity-70">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-[#FFE8E3] bg-white p-7" data-testid="subscription-panel">
        <h2 className="flex items-center gap-2 font-nunito text-lg font-bold tracking-tight text-[#2D3748]"><CreditCard className="h-5 w-5 text-[#FF7E67]" /> Subscription</h2>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-[#FFF4E6] px-3 py-1 font-nunito text-sm font-bold capitalize text-[#B45309]">{ent?.plan || user?.plan} plan</span>
          {ent?.trial && ent?.trial_ends_at && (
            <span className="flex items-center gap-1 rounded-full bg-[#E6FBF5] px-3 py-1 font-quicksand text-sm font-semibold text-[#06D6A0]">
              <Clock className="h-4 w-4" strokeWidth={2.5} /> Trial ends {new Date(ent.trial_ends_at).toLocaleDateString()}
            </span>
          )}
        </div>
        {ent && (
          <div className="mt-5 space-y-4">
            {[["coach_messages", "AI coach messages"], ["weekly_reviews", "Weekly reviews"], ["voice_minutes", "Voice minutes"]].map(([k, label]) => {
              const u = ent.usage[k]; if (!u) return null;
              const unlimited = u.limit === null;
              const pct = unlimited ? 0 : u.limit === 0 ? 100 : Math.min(100, Math.round((u.used / u.limit) * 100));
              return (
                <div key={k}>
                  <div className="mb-1 flex justify-between font-quicksand text-xs font-semibold text-[#718096]">
                    <span>{label}</span>
                    <span>{u.used}{unlimited ? "" : ` / ${u.limit}`}{unlimited ? " · unlimited" : ""}</span>
                  </div>
                  {!unlimited && (
                    <div className="h-2 w-full overflow-hidden rounded-full bg-[#FFE8E3]">
                      <div className="h-full rounded-full bg-sunrise" style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <button onClick={() => navigate("/pricing")} data-testid="manage-plan-btn"
          className="mt-5 flex items-center gap-2 rounded-full bg-sunrise px-6 py-3 text-sm font-bold text-white shadow-glow-coral">
          <Sparkles className="h-4 w-4" strokeWidth={2.5} /> {(ent?.plan || user?.plan) === "free" ? "Upgrade Plan" : "Manage Plan"}
        </button>
      </div>

      <p className="text-xs text-[#718096]">
        b→come provides educational coaching only. It is not a licensed therapist, doctor, dietitian, lawyer, or financial advisor.
      </p>
    </div>
  );
}
